import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import styles from "./PropertyPage.module.css";
import location from "../../assets/Location.svg";
import rating from "../../assets/star.svg";
import price from "../../assets/price.svg";
import BookingForm from "../../components/BookingForm/BookingForm";
import LocationSection from "../../components/LocationSection/LocationSection";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { Helmet } from "react-helmet";
import PopupForm from "../../components/PopupForm/PopupForm";
import { useRef } from "react";

const API_BASE = process.env.REACT_APP_API_BASE;
const URL_BASE = process.env.REACT_APP_FRONTEND_BASE;

const PropertyPage = () => {
  const { citySlug, propertySlug } = useParams();
  const [isModal, setIsModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [pageContext, setPageContext] = useState({
    cityId: "",
    microlocationId: "",
    propertyId: "", // ✅ Property-specific ID
    cityName: "",
    locationName: "",
  });

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allAmenities, setAllAmenities] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [selectedImage, setSelectedImage] = useState(0);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const carouselRef = useRef(null);

  const scrollToSlide = (index) => {
    if (!carouselRef.current) return;

    const slideWidth = carouselRef.current.clientWidth;
    carouselRef.current.scrollTo({
      left: slideWidth * index,
      behavior: "smooth",
    });

    setSelectedImage(index);
  };

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const handleScroll = () => {
      const slideWidth = el.clientWidth;
      const index = Math.round(el.scrollLeft / slideWidth);
      setSelectedImage(index);
    };

    el.addEventListener("scroll", handleScroll);

    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  const openEnquiry = useCallback(
    (propertyData = null) => {
      console.log("🚀 DEBUG - pageContext:", pageContext);
      setModalData({
        cityId: pageContext.cityId,
        microlocationId: pageContext.microlocationId,
        propertyId: pageContext.propertyId, // ✅ Property MongoDB ID
        city: pageContext.cityName,
        microlocation: pageContext.locationName,
        selectedProperty: propertyData || property, // Full property object
      });
      setIsModal(true);
    },
    [pageContext, property],
  );

  const WORD_LIMIT = 80;
  const { isLong, previewText } = useMemo(() => {
    if (!property?.description) {
      return { isLong: false, previewText: "" };
    }
    const words = property.description.trim().split(/\s+/);
    const long = words.length > WORD_LIMIT;
    const preview = long
      ? words.slice(0, WORD_LIMIT).join(" ")
      : property.description;

    return { isLong: long, previewText: preview };
  }, [property?.description]);

  const MAX_THUMBNAILS = 4;

  useEffect(() => {
    const fetchPropertyData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE}/api/properties/slug/${propertySlug}`,
        );
        const propertyData = res.data?.data || null;
        console.log(propertyData.location?.micro_locations[0]);
        setProperty(propertyData);

        if (propertyData) {
          const newContext = {
            cityId: propertyData.location?.city || "",
            microlocationId: propertyData.location?.micro_locations[0] || "", // ✅ KEY LINE
            propertyId: propertyData._id || "",
            cityName:
              propertyData.city?.displayCity ||
              propertyData.city?.city ||
              citySlug,
            locationName:
              propertyData.microlocation?.displayLocation ||
              propertyData.location?.address ||
              "",
          };
          console.log("🔍 DEBUG - Property pageContext:", newContext); // Debug
          setPageContext(newContext);
        }
      } catch (err) {
        console.error("Property fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPropertyData();
  }, [propertySlug, citySlug]);

  useEffect(() => {
    const fetchAmenities = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/amenities?all=true`);
        const list = res.data?.data ?? res.data ?? [];
        setAllAmenities(Array.isArray(list) ? list : []);
      } catch (e) {
        setAllAmenities([]);
      }
    };
    fetchAmenities();
  }, []);

  const amenitiesMap = useMemo(() => {
    const map = new Map();
    allAmenities.forEach((a) => {
      map.set(a._id, a);
    });
    return map;
  }, [allAmenities]);

  // Filter only amenities existing on this property
  const propertyAmenitiesDetails = property?.amenities
    ? property.amenities.map((id) => amenitiesMap.get(id)).filter(Boolean)
    : [];

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/plans?all=true`);
        const list = res.data?.data ?? res.data ?? [];
        setAllPlans(Array.isArray(list) ? list : []);
      } catch (error) {
        setAllPlans([]);
        console.error("Failed to fetch plans", error);
      }
    };
    fetchPlans();
  }, []);

  const plansMap = useMemo(() => {
    const map = new Map();
    allPlans.forEach((plan) => {
      map.set(plan._id, plan);
    });
    return map;
  }, [allPlans]);

  const enrichedPlans = property?.coliving_plans
    ? property.coliving_plans.map((cp) => {
        const planDetails = plansMap.get(cp.plan);
        return {
          ...cp,
          planDetails,
          type: planDetails?.type || "Plan",
          description: planDetails?.description || "",
          image: planDetails?.image?.secureUrl || "",
        };
      })
    : [];

  // Function to open lightbox
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Function to handle thumbnail click
  const handleThumbnailClick = (index) => {
    setSelectedImage(index);
    openLightbox(index);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <p>Loading property...</p>
      </div>
    );
  }

  if (!property) {
    return (
      <div className={styles.error}>
        <p>Property not found</p>
        <Link to="/">Go back to Home</Link>
      </div>
    );
  }

  const formatINR = (n) =>
    n == null
      ? ""
      : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
          Number(n),
        );

  const seoTitle =
    property.seo?.title?.trim() ||
    `${property.name} Coliving in ${
      property.city?.displayCity || property.city?.city || citySlug
    }`;

  const seoDescription =
    property.seo?.description?.trim() ||
    `Fully furnished coliving at ${property.name} in ${
      property.city?.displayCity || property.city?.city || citySlug
    } with modern amenities, flexible plans, and hassle-free living.`;

  const canonicalUrl = `${URL_BASE}/${citySlug}/${property.slug}`;

  // Open Graph (fallbacks to main image + SEO or generic)
  const ogTitle = property.seo?.open_graph?.title?.trim() || seoTitle;

  const ogDescription =
    property.seo?.open_graph?.description?.trim() || seoDescription;

  const ogImage =
    property.seo?.open_graph?.image?.trim() ||
    property.images?.[0]?.secureUrl ||
    `${URL_BASE}/og.png`;

  // Twitter (summary_large_image card)
  const twitterTitle = property.seo?.twitter?.title?.trim() || seoTitle;

  const twitterDescription =
    property.seo?.twitter?.description?.trim() || seoDescription;

  const twitterImage = property.seo?.twitter?.image?.trim() || ogImage;

  const robots = property.seo?.robots?.trim() || "index, follow";

  const keywords =
    Array.isArray(property.seo?.keywords) && property.seo.keywords.length
      ? property.seo.keywords.join(", ")
      : "";

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="robots" content={robots} />
        {keywords && <meta name="keywords" content={keywords} />}

        {/* Canonical */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="Vista Urban Living" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={twitterTitle} />
        <meta name="twitter:description" content={twitterDescription} />
        <meta name="twitter:image" content={twitterImage} />
      </Helmet>
      <div className={styles.propertyPage}>
        <div className={styles.breadcrumb}>
          <div className={`container ${styles.container}`}>
            <Link to="/" className={styles.breadcrumbLink}>
              Home
            </Link>
            <span className={styles.breadcrumbSeparator}>/</span>
            <Link
              to={`/coliving/${property.city?.slug || citySlug}`}
              className={styles.breadcrumbLink}
            >
              {property.city?.displayCity || property.city?.city || citySlug}
            </Link>
            <span className={styles.breadcrumbSeparator}>/</span>
            <span className={styles.breadcrumbCurrent}>{property.name}</span>
          </div>
        </div>
        <div className={styles.heroSection}>
          <div className={styles.propertyHeader}>
            <div className={`container ${styles.container}`}>
              <div className={styles.propertyHead}>
                <div>
                  <h1 className={styles.propertyTitle}>{property.name}</h1>
                  <p className={styles.propertyLocation}>
                    <span>
                      <img src={rating} alt="rating" />
                      {property.rating}
                    </span>
                    <span>|</span>
                    <span>
                      <img src={location} alt="location" />
                      {property.location.address}
                    </span>
                    <span>|</span>
                    <span>
                      <img src={price} alt="price" />
                      Best price <div className="deskHide">guarantee</div>
                    </span>
                  </p>
                </div>
                <div className={`deskHide ${styles.priceSection}`}>
                  <p>
                    Starting from
                    <span>
                      ₹{property.startingPrice.toLocaleString()}/month
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.propertyImages}>
            <div className={`container ${styles.propertyContainer}`}>
              <div className={styles.imageGallery}>
                {property.images?.length > 0 && (
                  <div
                    className={styles.largeImage}
                    onClick={() => openLightbox(0)}
                  >
                    {property.images && property.images.length > 0 && (
                      <img
                        src={property.images[0].secureUrl}
                        alt={property.name}
                      />
                    )}
                    <div className={styles.viewAllPhotos}>
                      View all {property.images.length} photos
                    </div>
                  </div>
                )}
                <div className={styles.smallImages}>
                  {property.images
                    ?.slice(1, MAX_THUMBNAILS + 1)
                    .map((img, idx) => (
                      <div
                        key={img.publicId}
                        className={`${styles.smallImage} ${
                          selectedImage === idx + 1 ? styles.active : ""
                        }`}
                        onClick={() => handleThumbnailClick(idx + 1)}
                      >
                        <img
                          src={img.secureUrl}
                          alt={`${property.name} Image ${idx + 2}`}
                        />
                      </div>
                    ))}
                </div>
              </div>
              {/* Mobile Carousel */}
              <div className={styles.mobileCarouselWrapper}>
                <div className={styles.mobileCarousel} ref={carouselRef}>
                  {(property.images || []).map((img, index) => (
                    <div
                      key={img.publicId}
                      className={styles.mobileSlide}
                    >
                      <img
                        src={img.secureUrl}
                        alt={`${property.name} ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>

                {/* Dots OUTSIDE scroll container */}
                <div className={styles.dots}>
                  {property.images?.slice(0, 4).map((_, index) => (
                    <span
                      key={index}
                      className={`${styles.dot} ${
                        selectedImage === index ? styles.activeDot : ""
                      }`}
                      onClick={() => scrollToSlide(index)}
                    />
                  ))}
                </div>
              </div>

              {/* Lightbox Component - Shows ALL images */}
              <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                index={lightboxIndex}
                slides={(property.images || []).map((img, index) => ({
                  src: img.secureUrl,
                  alt: `${property.name} - Image ${index + 1}`,
                  width: 1200,
                  height: 800,
                }))}
                plugins={[Thumbnails, Zoom, Fullscreen]}
                thumbnails={{
                  position: "bottom",
                  width: 120,
                  height: 80,
                  border: 0,
                  borderRadius: 8,
                  padding: 4,
                  gap: 8,
                }}
                zoom={{
                  maxZoomPixelRatio: 3,
                  zoomInMultiplier: 2,
                  doubleTapDelay: 300,
                  doubleClickDelay: 300,
                  doubleClickMaxStops: 2,
                  keyboardMoveDistance: 50,
                  wheelZoomDistanceFactor: 100,
                  pinchZoomDistanceFactor: 100,
                  scrollToZoom: true,
                }}
                carousel={{
                  finite: true,
                  preload: 2,
                  padding: "16px",
                  spacing: "30%",
                  imageFit: "contain",
                }}
                render={{
                  buttonPrev:
                    property.images.length <= 1 ? () => null : undefined,
                  buttonNext:
                    property.images.length <= 1 ? () => null : undefined,
                }}
                controller={{
                  closeOnBackdropClick: true,
                  closeOnPullDown: true,
                  closeOnPullUp: true,
                }}
              />
            </div>
          </div>
        </div>
        <div className={styles.propertyContent}>
          <div className={`container ${styles.container}`}>
            <div className={styles.contentGrid}>
              <div className={styles.leftContent}>
                <section className={styles.propertyAbout}>
                  <div className={styles.propertyDescription}>
                    <p className={styles.descriptionText}>
                      {showFullDescription
                        ? property.description
                        : `${previewText}${isLong ? "..." : ""}`}
                    </p>

                    {isLong && (
                      <button
                        className={styles.showMoreBtn}
                        aria-expanded={showFullDescription}
                        onClick={() => setShowFullDescription((s) => !s)}
                      >
                        {showFullDescription ? "Show Less" : "Show More"}
                      </button>
                    )}
                  </div>
                  <hr className={styles.headerline} />
                  <div className={styles.amenitiesSection}>
                    <h2 className={styles.sectionTitle}>Amenities</h2>
                    <div className={styles.amenitiesGrid}>
                      {propertyAmenitiesDetails
                        .filter((amenity) => amenity && amenity._id)
                        .map((amenity) => (
                          <div key={amenity._id} className={styles.amenityItem}>
                            {amenity.icon?.secureUrl ? (
                              <img
                                className={styles.amenityIcon}
                                src={amenity.icon.secureUrl}
                                alt={amenity.label}
                              />
                            ) : (
                              <span className={styles.amenityIcon}>
                                {amenity.icon}
                              </span> // fallback if icon is string or emoji
                            )}
                            <span className={styles.amenityLabel}>
                              {amenity.name}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                </section>
                <section className={styles.roomsSection}>
                  <div className={styles.container}>
                    <div className={styles.roomsHeader}>
                      <div>
                        <h2 className={styles.sectionTitle}>Coliving Rooms</h2>
                        <p className={styles.sectionSubtitle}>
                          Fully furnished private room & sharing room with
                          access to shared amenities
                        </p>
                      </div>
                      <button
                        type="button"
                        className={`deskHide ${styles.checkBtn}`}
                        onClick={() => openEnquiry(property)}
                        aria-label="Check availability"
                      >
                        Check availability
                      </button>
                    </div>

                    <div className={styles.roomsList}>
                      {Array.isArray(enrichedPlans) &&
                        enrichedPlans.map((room, index) => (
                          <article
                            key={room._id || room.plan}
                            className={styles.roomCard}
                            role="group"
                            aria-labelledby={`room-${room._id}-title`}
                          >
                            {/* Left: Image */}
                            <div className={styles.roomImage}>
                              {property.images &&
                              property.images.length > index ? (
                                <img
                                  src={property.images[index].secureUrl}
                                  alt={property.name}
                                />
                              ) : (
                                <img
                                  src="/default-room.jpg"
                                  alt="Default Room"
                                />
                              )}
                            </div>

                            {/* Middle: Room info */}
                            <div className={styles.roomDiv}>
                              <div className={styles.roomInfo}>
                                <div className={styles.roomHead}>
                                  <h3
                                    id={`room-${room.id}-title`}
                                    className={styles.roomType}
                                  >
                                    {room.type}
                                  </h3>
                                </div>

                                <p
                                  className={`deskHide ${styles.roomDescription}`}
                                >
                                  {room.description}
                                </p>

                                {/* Feature chips */}
                                <div className={styles.featureChips}>
                                  {Array.isArray(room.features) &&
                                    room.features.map((feat, i) => (
                                      <span key={i} className={styles.chip}>
                                        {feat}
                                      </span>
                                    ))}
                                </div>

                                {/* CTA (mobile-first position) */}
                                <div className={styles.mobileCta}>
                                  <button
                                    type="button"
                                    className={styles.enquireBtn}
                                    onClick={() =>
                                      document
                                        .getElementById("booking-form")
                                        ?.scrollIntoView({ behavior: "smooth" })
                                    }
                                  >
                                    Enquire Now
                                  </button>
                                </div>
                              </div>

                              {/* Right: Price */}
                              <div className={styles.roomPriceWrap}>
                                <span className={styles.priceLabel}>
                                  Starting Price
                                </span>
                                <div className={styles.priceRow}>
                                  <span className={styles.priceSymbol}>₹</span>
                                  <span className={styles.priceValue}>
                                    {formatINR(room.price)}
                                  </span>
                                  <span className={styles.priceUnit}>
                                    / month
                                  </span>
                                </div>
                                {room.originalPrice && (
                                  <div className={styles.strikeRow}>
                                    <span className={styles.strike}>
                                      ₹{formatINR(room.originalPrice)}
                                    </span>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className={styles.enquireBtn}
                                  onClick={() => openEnquiry(property)}
                                >
                                  Enquire Now
                                </button>
                              </div>
                            </div>
                          </article>
                        ))}
                    </div>
                  </div>
                </section>
                <section className={styles.locationSection}>
                  <div className={styles.container}>
                    <div className={styles.roomsHeader}>
                      <div>
                        <h2 className={styles.sectionTitle}>Location</h2>
                        <p className={styles.sectionSubtitle}>
                          Stay connected to everything you need with a central
                          location
                        </p>
                      </div>
                    </div>
                    <LocationSection property={property} />
                  </div>
                </section>
              </div>
              <div className={`deskHide ${styles.rightContent}`}>
                <BookingForm property={property} roomTypes={enrichedPlans} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={`mobHide ${styles.fixedBottomFooter}`}>
        <div className={styles.priceSection}>
          <p>
            Starting from
            <span>₹{property.startingPrice.toLocaleString()}/month</span>
          </p>
        </div>
        <button
          type="button"
          className={styles.enquireBtn}
          onClick={() => openEnquiry(property)}
        >
          Enquire Now
        </button>
      </div>
      <PopupForm
        city={modalData?.cityId}
        microlocation={modalData?.microlocationId}
        property={modalData?.selectedProperty || property}
        roomTypes={enrichedPlans}
        open={isModal}
        onClose={() => setIsModal(false)}
      />
    </>
  );
};

export default PropertyPage;
